import React, {useMemo} from "react";

import {Form} from "react-final-form";

import usePaymentGroups from "@globals/hooks/usePaymentGroups";

import Button from "@components/Button";
import LoadingDots from "@components/LoadingDots";
import {
  RadioButtonsAlignment,
  RadioButtonsField,
  TextField,
  TextareaField,
} from "@components/forms";

import {SamlConfiguration} from "../interfaces";
import useSamlConfiguration from "../useSamlConfiguration";
import styles from "./SamlConfigurationForm.scss";

export default function SamlConfigurationForm(): JSX.Element {
  const {data, save, isLoading, isError, isSaved} = useSamlConfiguration();
  const {data: pgData, isPending: isPGLoading} = usePaymentGroups();

  const pgOptions = useMemo(() => {
    if (!pgData) {
      return [];
    }
    const options = pgData.map(({id, description}) => ({
      value: id,
      label: description,
    }));

    return [{value: "", label: "No Default"}, ...options];
  }, [pgData]);

  if (isPGLoading || (isLoading && data === undefined)) {
    return <LoadingDots />;
  }

  return (
    <Form
      onSubmit={(data: SamlConfiguration) => save(data)}
      initialValues={data}
      render={({handleSubmit, invalid}) => (
        <form onSubmit={handleSubmit}>
          <TextField
            name="saml_url"
            label="Identity Provider Single Sign-On URL"
          />
          <TextField name="saml_entity_id" label="Identity Provider Issuer" />
          <TextareaField
            name="saml_cert"
            label="X.509 Certificate"
            inputClassName={styles.samlCertField}
          />
          <p>
            Also you may select a default program for new users logging in with
            SAML.
            <br />
            If selected, new users will be assigned to this program when first
            logging in.
            <br />
            If No Default is set, then new users will need to be accepted or
            invited by an admin from the admin dashboard.
          </p>
          <RadioButtonsField
            name="saml_default_pg_id"
            legendText="Payment group"
            options={pgOptions}
            alignment={RadioButtonsAlignment.Vertical}
          />
          <Button
            type="submit"
            disabled={isLoading || invalid}
            className={styles.submitButton}
          >
            Save SAML Configuration Changes
          </Button>
          {isLoading && <span>Loading ...</span>}
          {isSaved && <span>Saved changes</span>}
          {isError && (
            <span>
              There was an issue saving your changes, please refresh and try
              again
            </span>
          )}
        </form>
      )}
    ></Form>
  );
}
